// See NotificationBar.spec.md for NB-FR-001 through NB-FR-009

import { ClickAwayListener } from '@material-ui/core'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import useReactRouter from 'use-react-router'
import { TIMEFRAME_OPTIONS } from 'core/audit-events/timeframes'
import useAuditEventStream from 'core/audit-events/useAuditEventStream'
import useEventSessionScope from 'core/audit-events/useEventSessionScope'
import useEventTimeframe from 'core/audit-events/useEventTimeframe'
import PollingData, { POLLING_INTERVALS } from 'core/components/PollingData'
import TimePicklist from 'core/components/widgets/TimePicklist'
import Button from 'core/elements/button/Button'
import IconButton from 'core/elements/button/IconButton'
import Input from 'core/elements/input/Input'
import Text from 'core/elements/Text'
import VisuallyHidden from 'core/elements/VisuallyHidden'
import { routeManager } from 'core/utils/routes'
import { formatUnreadCount } from './helpers'
import NotificationBarDrawer, { COLLAPSED_BAR_HEIGHT } from './NotificationBarDrawer'
import useNotificationBarStyles from './notificationBarStyles'
import useDrawerLayout from './useDrawerLayout'
import useLastSeenEvents from './useLastSeenEvents'

const DRAWER_ID = 'notification-bar-drawer'
// DefaultFrame's header row is at least this tall; the drawer opens over everything below it
const FRAME_HEADER_HEIGHT = 65
// NB-FR-003: The bar polls every 15 s for its badge and top 25 rows, so each request is capped
const BAR_EVENTS_CAP = 1000

const getDrawerMaxHeight = () => window.innerHeight - FRAME_HEADER_HEIGHT - COLLAPSED_BAR_HEIGHT

interface Props {
  // NB-FR-001: Reports the bar's height (with an open drawer) so floating elements stay above it
  onHeightChange?: (height: number) => void
}

export default function NotificationBar({ onHeightChange }: Props) {
  const classes = useNotificationBarStyles()
  const barRef = useRef<HTMLElement>(null)
  const { history } = useReactRouter()
  // ETF-FR-001: Shared with the Notification Center page and kept in the user's preferences
  const [timeframe, setTimeframe] = useEventTimeframe()
  const [search, setSearch] = useState('')
  // NB-FR-004: The drawer opens over the page, up to the app header (kept visible for its region
  // and tenant pickers)
  const layout = useDrawerLayout(getDrawerMaxHeight)
  const { open, openDrawer, closeDrawer } = layout
  const { activeRegion, activeTenantId } = useEventSessionScope()
  // NB-FR-003: The same loader as the page; polls only ask for events newer than the loaded ones
  const { events, loading, error, lastUpdated, refetch, refreshInBackground } = useAuditEventStream(
    { timeframe, limit: BAR_EVENTS_CAP },
  )
  const lastSeen = useLastSeenEvents(events, { eventsLoaded: lastUpdated !== null })
  const { markAllRead, lastSeenAt, refresh: refreshLastSeen } = lastSeen
  // NB-FR-003, NB-FR-007: Every poll re-fetches the events and re-reads the last-seen time saved
  // on other machines. PollingData pauses while the tab is hidden or the user is idle. Stable, so
  // typing or dragging doesn't make PollingData rebuild its interval.
  const poll = useCallback(async () => {
    await Promise.all([refreshInBackground(), refreshLastSeen()])
  }, [refreshInBackground, refreshLastSeen])

  // NB-FR-005: lastSeenAt when this opening of the drawer started (toggle, search, drag or bar
  // click); rows newer than it stay marked as new until it closes. undefined while closed, or
  // until the last-seen time is known. Adjusted during render, so it is never a commit behind.
  const [cutoff, setCutoff] = useState<string>()
  if (!open && cutoff !== undefined) setCutoff(undefined)
  else if (open && cutoff === undefined && lastSeenAt !== null) setCutoff(lastSeenAt)
  const highlightCutoff = open ? cutoff : undefined

  // NB-FR-008: Collapsing the drawer (toggle or drag) clears the search. Only the moment it
  // collapses: text typed while it is already collapsed stays.
  const [wasOpen, setWasOpen] = useState(open)
  if (wasOpen !== open) {
    setWasOpen(open)
    if (!open) setSearch('')
  }

  // NB-FR-007: While the drawer is open, what it shows is seen — on opening and whenever new
  // events arrive. It waits for this opening's cutoff, so the rows that were new stay marked.
  useEffect(() => {
    if (highlightCutoff !== undefined) markAllRead(events)
  }, [highlightCutoff, events, markAllRead])

  // NB-FR-001: Report the bar's height whenever it changes (drawer opened, resized, closed); at
  // most once a frame while the drawer is dragged
  useEffect(() => {
    const bar = barRef.current
    if (!onHeightChange || !bar) return undefined
    onHeightChange(bar.offsetHeight)
    if (typeof ResizeObserver === 'undefined') return undefined
    let frame: number | null = null
    const observer = new ResizeObserver(() => {
      if (frame !== null) return
      frame = requestAnimationFrame(() => {
        frame = null
        onHeightChange(bar.offsetHeight)
      })
    })
    observer.observe(bar)
    return () => {
      observer.disconnect()
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [onHeightChange])

  // NB-FR-006: The badge is cleared while the drawer is open
  const unreadCount = open ? 0 : lastSeen.unreadCount
  const unreadText = formatUnreadCount(unreadCount)
  const notificationCenterPath = routeManager.routes.openstack.notificationCenter.path()

  return (
    // NB-FR-009: Pressing anywhere outside the bar closes an open drawer. Mousedown (not click),
    // so a drag that starts on the handle and ends over the page doesn't count; menus the bar
    // opens in portals (the timeframe dropdown) still count as inside.
    <ClickAwayListener
      mouseEvent={open ? 'onMouseDown' : false}
      touchEvent={open ? 'onTouchStart' : false}
      onClickAway={closeDrawer}
    >
      <section className={classes.bar} aria-label="Notifications" ref={barRef}>
        {/* NB-FR-004: Always on the bar's top edge, so a collapsed bar can be dragged open */}
        {/* biome-ignore lint/a11y/useSemanticElements: a focusable resize handle is a separator widget, which <hr> can't be */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize events drawer"
          aria-controls={DRAWER_ID}
          aria-valuenow={open ? layout.height : 0}
          aria-valuemin={0}
          aria-valuemax={layout.maxHeight}
          tabIndex={0}
          className={classes.handle}
          {...layout.handleProps}
        />
        <PollingData
          hidden
          loading={loading}
          onReload={poll}
          pollIntervalMs={POLLING_INTERVALS.FIFTEEN_SECONDS}
          refreshDuration={POLLING_INTERVALS.FIFTEEN_SECONDS}
        />
        {/* NB-FR-004: Clicking anywhere on a collapsed bar opens the drawer (the timeframe dropdown
          too); only the Notification Center button leaves. Keyboard users have the toggle. */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: the toggle button is the keyboard control */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: a mouse convenience over real controls */}
        <div
          className={clsx(classes.controls, { [classes.controlsCollapsed]: !open })}
          onClick={open ? undefined : openDrawer}
        >
          <div className={classes.group}>
            <IconButton
              icon={open ? 'chevron-down' : 'chevron-up'}
              // Figma: a thin (light) chevron, smaller than the default icon button glyph
              solid={false}
              className={classes.toggle}
              aria-label={open ? 'Hide events' : 'Show events'}
              aria-expanded={open}
              aria-controls={DRAWER_ID}
              onClick={open ? closeDrawer : openDrawer}
            />
            {/* NB-FR-002: Styled like the active tab of the app's tab strips. A plain label until
              there is a second notification type to switch to. */}
            <div className={classes.tab} data-testid="events-label">
              <span className={classes.tabLabel}>
                <Text variant="subtitle2" component="span" className={classes.tabText}>
                  Events
                </Text>
                {unreadText && (
                  <>
                    <span className={classes.count} data-testid="unread-count" aria-hidden>
                      {unreadText}
                    </span>
                    <VisuallyHidden>
                      {`${unreadCount} unread ${unreadCount === 1 ? 'event' : 'events'}`}
                    </VisuallyHidden>
                    <span className={classes.dot} data-testid="unread-dot" aria-hidden />
                  </>
                )}
              </span>
              {/* NB-FR-002: Underlined only while the drawer is open, so the closed bar stays quiet */}
              {open && <span className={classes.tabUnderline} aria-hidden />}
            </div>
          </div>
          <div className={classes.group}>
            <Input
              compact
              icon="search"
              className={classes.search}
              placeholder="Search events..."
              aria-label="Search events"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              // NB-FR-008: Using the search opens the drawer like the toggle; focus stays here.
              // Input sets its own onFocus, so the capture phase is used.
              onFocusCapture={() => {
                if (!open) openDrawer()
              }}
            />
            <TimePicklist
              currentTimeRange={timeframe}
              onChange={setTimeframe}
              options={TIMEFRAME_OPTIONS}
              className={classes.timeframe}
              // The bar's controls sit at the right of the window; open the menu towards the left
              menuAlign="right"
            />
            <span className={classes.divider} aria-hidden />
            <Button
              variant="secondary"
              icon="bell"
              onClick={(event) => {
                // Leaves for the page instead of opening the drawer (NB-FR-004)
                event.stopPropagation()
                history.push(notificationCenterPath)
              }}
            >
              Notification Center
              <span className={classes.betaTag}>BETA</span>
            </Button>
          </div>
        </div>
        {open && (
          <NotificationBarDrawer
            id={DRAWER_ID}
            events={events}
            highlightCutoff={highlightCutoff ?? null}
            height={layout.height}
            search={search}
            notificationCenterPath={notificationCenterPath}
            loading={loading}
            error={error}
            onRetry={refetch}
            activeRegion={activeRegion}
            activeTenantId={activeTenantId}
          />
        )}
      </section>
    </ClickAwayListener>
  )
}
